import {
  ActivitySpinnerSize,
  ActivitySpinnerWidget,
  CheckBoxWidget,
  CheckBoxWithLabelWidget,
  CompactInlineAlertWidget,
  InlineAlertType,
} from '@tableau/widgets';
import * as React from 'react';

import { AdminProfileSiteManagementDialogStyles } from '~src/adminProfiles/styles/AdminProfileSiteManagementDialogStyles';
import { Messages } from '~src/codegen/Localize';
import { Formatters } from '~src/codegen/Localize';
import { CheckboxState } from '~src/commonInfra/components/checkbox/CheckboxState';
import { ReactActionDialogBodyProps } from '~src/commonInfra/dialogs/ReactActionDialog';

import { ISiteNameWithId } from '../../siteManagement/serverApi/types/ISiteNameWithId';
import { IAdminProfileSite } from '../models/IAdminProfile';

export type AdminProfileSiteManagementDialogProps = {
  readonly profileId: string;
  readonly profileName: string;
  readonly sitesWhereEnabled: ReadonlyArray<IAdminProfileSite>;
  readonly onProfileSitesUpdated?: () => void;
};

export type AdminProfileSiteManagementDialogState = {
  readonly sites: ISiteNameWithId[];
  readonly siteCheckboxState: Record<string, CheckboxState>;
  readonly assignedUsersBySite: Record<string, number>;
  readonly loading: boolean;
};

export type AdminProfileSiteManagementDialogBodyProps = ReactActionDialogBodyProps<
  AdminProfileSiteManagementDialogProps,
  AdminProfileSiteManagementDialogState
>;

/**
 * Returns sites that have assigned users and are being disabled (unchecked) in the dialog.
 * These sites warrant a warning because removing the profile will affect existing users.
 */
function getSitesWithAssignedUsersBeingDisabled(
  props: AdminProfileSiteManagementDialogBodyProps,
  dialogState: AdminProfileSiteManagementDialogState
): ISiteNameWithId[] {
  const { siteCheckboxState, assignedUsersBySite, sites } = dialogState;
  const enabledSiteIds = new Set(props.sitesWhereEnabled.map((s) => s.id));
  return sites.filter(
    (s) =>
      enabledSiteIds.has(s.id) &&
      siteCheckboxState[s.id] === CheckboxState.empty &&
      (assignedUsersBySite[s.id] ?? 0) > 0
  );
}

/**
 * Returns true if the user has changed which sites are selected compared to the initial state.
 * Used to enable/disable the Save button when there are unsaved changes.
 */
export function hasSiteSelectionChanged(
  props: Pick<AdminProfileSiteManagementDialogProps, 'sitesWhereEnabled'>,
  dialogState: AdminProfileSiteManagementDialogState
): boolean {
  const enabledSiteIds = new Set(props.sitesWhereEnabled.map((s) => s.id));
  const { siteCheckboxState, sites } = dialogState;
  for (const site of sites) {
    const isChecked = siteCheckboxState[site.id] === CheckboxState.checked;
    const wasEnabled = enabledSiteIds.has(site.id);
    if (isChecked !== wasEnabled) return true;
  }
  return false;
}

export function AdminProfileSiteManagementDialogBody(props: AdminProfileSiteManagementDialogBodyProps): JSX.Element {
  const {
    sites,
    siteCheckboxState,
    assignedUsersBySite,
    loading,
  } = props.dialogState;
  const { updateDialogState } = props;

  const toggleSite = React.useCallback(
    (site: ISiteNameWithId, checked: boolean) => {
      const newCheckboxState = { ...siteCheckboxState };
      newCheckboxState[site.id] = checked ? CheckboxState.checked : CheckboxState.empty;
      updateDialogState({ siteCheckboxState: newCheckboxState });
    },
    [siteCheckboxState, updateDialogState]
  );

  const toggleSelectAll = React.useCallback(
    (checked: boolean) => {
      const newCheckboxState: Record<string, CheckboxState> = {};
      for (const site of sites) {
        newCheckboxState[site.id] = checked ? CheckboxState.checked : CheckboxState.empty;
      }
      updateDialogState({ siteCheckboxState: newCheckboxState });
    },
    [sites, updateDialogState]
  );

  /** Form state: whether this site is currently checked (user can toggle on/off) */
  const isSiteChecked = (site: ISiteNameWithId): boolean =>
    siteCheckboxState[site.id] === CheckboxState.checked;

  const sitesWithAssignedUsersBeingDisabled = getSitesWithAssignedUsersBeingDisabled(props, props.dialogState);
  const hasWarningSitesBeingUnchecked = sitesWithAssignedUsersBeingDisabled.length > 0;
  const isRowWarning = (site: ISiteNameWithId): boolean =>
    sitesWithAssignedUsersBeingDisabled.some((s) => s.id === site.id);

  if (loading) {
    return (
      <div
        className={AdminProfileSiteManagementDialogStyles.DialogContentWrapper}
        data-tb-test-id="admin-profile-site-management-dialog"
      >
        <div style={AdminProfileSiteManagementDialogStyles.LoadingSpinnerContainer}>
          <ActivitySpinnerWidget spinnerSize={ActivitySpinnerSize.Large} />
        </div>
      </div>
    );
  }

  const selectedCount = sites.filter((s) => siteCheckboxState[s.id] === CheckboxState.checked).length;
  const totalCount = sites.length;

  return (
    <div
      className={AdminProfileSiteManagementDialogStyles.DialogContentWrapper}
      data-tb-test-id="admin-profile-site-management-dialog"
    >
      <div className={AdminProfileSiteManagementDialogStyles.SitesSection}>
        <div style={AdminProfileSiteManagementDialogStyles.SitesContainer}>
          <div style={AdminProfileSiteManagementDialogStyles.SitesTableHeader}>
            <span className={AdminProfileSiteManagementDialogStyles.SiteHeaderLabel}>
              <CheckBoxWithLabelWidget
                checked={selectedCount === totalCount}
                indeterminate={selectedCount > 0 && selectedCount < totalCount}
                handleChange={(checked: boolean) => toggleSelectAll(checked)}
                label={Messages.site()}
                disabled={props.actionBusy}
                testId="admin-profile-site-management-select-all"
              />
            </span>
            <span style={AdminProfileSiteManagementDialogStyles.AssignedUsersHeader}>
              {Messages.adminProfileSitesColumnAssignedUsers()}
            </span>
          </div>
          {sites.map((site, idx) => {
            const isWarning = isRowWarning(site);
            const rowStyle =
              idx < sites.length - 1
                ? {
                    ...AdminProfileSiteManagementDialogStyles.SitesTableRow,
                    ...(isWarning ? AdminProfileSiteManagementDialogStyles.warningRowBackground : {}),
                  }
                : {
                    ...AdminProfileSiteManagementDialogStyles.SitesTableRowLast,
                    ...(isWarning ? AdminProfileSiteManagementDialogStyles.warningRowBackground : {}),
                  };
            const assignedUsersCount = assignedUsersBySite[site.id] ?? 0;
            return (
              <div key={site.id} style={rowStyle}>
                <div style={AdminProfileSiteManagementDialogStyles.SiteCell}>
                  <CheckBoxWidget
                    checked={isSiteChecked(site)}
                    handleChange={(checked: boolean) => toggleSite(site, checked)}
                    disabled={props.actionBusy}
                    testId={`admin-profile-site-management-site-${site.id}`}
                  />
                  <span style={AdminProfileSiteManagementDialogStyles.SiteNameText}>
                    {site.name ?? site.urlName}
                  </span>
                </div>
                <div style={AdminProfileSiteManagementDialogStyles.AssignedUsersCell}>
                  {isWarning ? (
                    <span className={AdminProfileSiteManagementDialogStyles.WarningBadge}>
                      {assignedUsersCount}
                    </span>
                  ) : (
                    Formatters.formatCount(assignedUsersCount)
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {hasWarningSitesBeingUnchecked && (
        <div className={AdminProfileSiteManagementDialogStyles.WarningSection}>
          <CompactInlineAlertWidget
            testId="admin-profile-site-management-warning"
            type={InlineAlertType.Warning}
          >
            {Messages.adminProfileSiteManagementWarning()}
          </CompactInlineAlertWidget>
        </div>
      )}
    </div>
  );
}
